import { type JSX, type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { IntlErrorCode } from '@formatjs/intl';
import { flatten } from 'flat';
import { IntlProvider } from 'react-intl';
import { useLocation } from 'react-router';
import { findLocaleFromUrl } from './utils';
import FullPageLoadingIndicator from 'components/v2/FullPageLoadingIndicator';
import { loadZodLocale } from 'utils/modules/intl';

const defaultLocale = 'en-GB';

export default function PublicSupplyChainIntlProvider({ children }: PropsWithChildren): JSX.Element {
   const location = useLocation();
   const [messages, setMessages] = useState<Record<string, unknown> | null>(null);
   const [activeLocale, setActiveLocale] = useState<string | null>(null);

   const urlLocale = useMemo(() => findLocaleFromUrl(location.search), [location.search]);

   const resolvedLocale = useMemo(() => {
      if (urlLocale === 'en') return defaultLocale;

      return urlLocale ?? defaultLocale;
   }, [urlLocale]);

   useEffect(() => {
      let isActive = true;

      const loadMessages = async (): Promise<void> => {
         const fallbackLocale = defaultLocale;

         try {
            const { default: nextMessages } = await import(`lang/${resolvedLocale.substring(0, 2)}.json`);
            if (!isActive) return;
            setMessages(nextMessages);
            setActiveLocale(resolvedLocale);
         } catch (error) {
            console.error(error);

            try {
               const { default: fallbackMessages } = await import(`lang/${fallbackLocale.substring(0, 2)}.json`);
               if (!isActive) return;
               setMessages(fallbackMessages);
               setActiveLocale(fallbackLocale);
            } catch (fallbackError) {
               console.error(fallbackError);
               if (!isActive) return;
               setMessages({});
               setActiveLocale(fallbackLocale);
            }
         }
      };

      void loadMessages();

      return () => {
         isActive = false;
      };
   }, [resolvedLocale]);

   useEffect(() => {
      if (!activeLocale) return;

      document.documentElement.lang = activeLocale;
      loadZodLocale(activeLocale);
   }, [activeLocale]);

   if (!activeLocale || !messages) return <FullPageLoadingIndicator />;

   return (
      <IntlProvider
         locale={activeLocale}
         messages={flatten(messages)}
         defaultLocale={defaultLocale.substring(0, 2)}
         onError={
            process.env.REACT_APP_WARN_DEV_ERRORS === 'true'
               ? (error) => {
                    if (error.code === IntlErrorCode.MISSING_TRANSLATION) {
                       return;
                    }
                    console.error(error);
                 }
               : undefined
         }
      >
         {children}
      </IntlProvider>
   );
}
